import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";

import { getConfigurationManager } from "./configuration";
import {
  PlanDesignerConfiguration,
  PlanDesignerFeatureFlags,
} from "./types/configuration.types";

/**
 * Context type for Plan Designer configuration
 */
export interface PlanDesignerContextType {
  /** Current merged configuration */
  configuration: PlanDesignerConfiguration;

  /** Check if a specific feature is enabled */
  isFeatureEnabled: (feature: keyof PlanDesignerFeatureFlags) => boolean;

  /** Get feature flags */
  getFeatureFlags: () => NonNullable<PlanDesignerConfiguration["featureFlags"]>;

  /** Get theme configuration */
  getThemeConfig: () => NonNullable<PlanDesignerConfiguration["theme"]>;

  /** Get data configuration */
  getDataConfig: () => NonNullable<PlanDesignerConfiguration["data"]>;

  /** Get grid configuration */
  getGridConfig: () => NonNullable<PlanDesignerConfiguration["grid"]>;

  /** Get UI configuration */
  getUIConfig: () => NonNullable<PlanDesignerConfiguration["ui"]>;

  /** Get performance configuration */
  getPerformanceConfig: () => NonNullable<
    PlanDesignerConfiguration["performance"]
  >;

  /** Get validation configuration */
  getValidationConfig: () => NonNullable<
    PlanDesignerConfiguration["validation"]
  >;

  /** Get environment configuration */
  getEnvironment: () => NonNullable<PlanDesignerConfiguration["environment"]>;
}

/**
 * React Context for Plan Designer configuration
 * Exported for use in hooks that need to check context availability
 */
export const PlanDesignerContext = createContext<
  PlanDesignerContextType | undefined
>(undefined);

/**
 * Props for the Plan Designer Provider
 */
interface PlanDesignerProviderProps {
  /** Child components */
  children: ReactNode;

  /** Optional external configuration to override defaults */
  configuration?: Partial<PlanDesignerConfiguration>;
}

/**
 * Plan Designer Configuration Provider
 * Provides configuration values to all child components
 */
export const PlanDesignerProvider: React.FC<PlanDesignerProviderProps> = ({
  children,
  configuration: externalConfiguration,
}) => {
  // Initialize configuration manager with external config before first render
  const configManager = getConfigurationManager();

  if (externalConfiguration) {
    configManager.setExternalConfiguration(externalConfiguration);
  }

  const [configuration, setConfiguration] = useState<PlanDesignerConfiguration>(
    () => configManager.getConfiguration(),
  );

  // Update state when externalConfiguration changes
  useEffect(() => {
    if (externalConfiguration) {
      getConfigurationManager().setExternalConfiguration(externalConfiguration);
      // Set merged configuration
      setConfiguration(getConfigurationManager().getConfiguration());
    }
  }, [externalConfiguration]);

  // Create context value with helper functions
  const contextValue: PlanDesignerContextType = {
    configuration,

    isFeatureEnabled: (feature) =>
      getConfigurationManager().isFeatureEnabled(
        feature as keyof NonNullable<PlanDesignerConfiguration["featureFlags"]>,
      ),

    getFeatureFlags: () => getConfigurationManager().getFeatureFlags(),

    getThemeConfig: () => getConfigurationManager().getThemeConfig(),

    getDataConfig: () => getConfigurationManager().getDataConfig(),

    getGridConfig: () => getConfigurationManager().getGridConfig(),

    getUIConfig: () => getConfigurationManager().getUIConfig(),

    getPerformanceConfig: () =>
      getConfigurationManager().getPerformanceConfig(),

    getValidationConfig: () => getConfigurationManager().getValidationConfig(),

    getEnvironment: () => getConfigurationManager().getEnvironment(),
  };

  return (
    <PlanDesignerContext.Provider value={contextValue}>
      {children}
    </PlanDesignerContext.Provider>
  );
};

/**
 * Hook to use Plan Designer configuration context
 * @returns Configuration context with helper functions
 * @throws Error if used outside of PlanDesignerProvider
 */
export const usePlanDesignerConfiguration = (): PlanDesignerContextType => {
  const context = useContext(PlanDesignerContext);

  if (context === undefined) {
    throw new Error(
      "usePlanDesignerConfiguration must be used within a PlanDesignerProvider",
    );
  }

  return context;
};

/**
 * Hook to get feature flags specifically
 * Convenience hook for accessing feature flags
 */
export const usePlanDesignerFeatureFlags = (): NonNullable<
  PlanDesignerConfiguration["featureFlags"]
> => {
  const { getFeatureFlags } = usePlanDesignerConfiguration();
  return getFeatureFlags();
};

/**
 * Hook to check if a specific feature is enabled
 * Convenience hook for feature flag checks
 */
export const useFeatureFlag = (
  feature: keyof PlanDesignerFeatureFlags,
): boolean => {
  const { isFeatureEnabled } = usePlanDesignerConfiguration();
  return isFeatureEnabled(feature);
};

/**
 * Hook to get theme configuration
 * Convenience hook for theme settings
 */
export const usePlanDesignerTheme = (): NonNullable<
  PlanDesignerConfiguration["theme"]
> => {
  const { getThemeConfig } = usePlanDesignerConfiguration();
  return getThemeConfig();
};

/**
 * Hook to get grid configuration
 * Convenience hook for grid settings
 */
export const usePlanDesignerGrid = (): NonNullable<
  PlanDesignerConfiguration["grid"]
> => {
  const { getGridConfig } = usePlanDesignerConfiguration();
  return getGridConfig();
};

/**
 * Hook to get UI configuration
 * Convenience hook for UI settings
 */
export const usePlanDesignerUI = (): NonNullable<
  PlanDesignerConfiguration["ui"]
> => {
  const { getUIConfig } = usePlanDesignerConfiguration();
  return getUIConfig();
};

/**
 * Hook to get RFP identifiers from configuration
 * Convenience hook for accessing RFP IDs
 */
export const usePlanDesignerRfpIds = (): {
  rfpId: string | undefined;
  ecoRfpId: string | undefined;
} => {
  const { configuration } = usePlanDesignerConfiguration();
  return {
    rfpId: configuration.rfpId,
    ecoRfpId: configuration.ecoRfpId,
  };
};

// Set display name for better debugging
PlanDesignerProvider.displayName = "PlanDesignerProvider";
